import { InsurerFormValues } from '@/core/domain/people/Insurers/Insurers.types';
import { useDefaultFormErrorNotification, useServiceError } from '@/hooks';
import { Form } from 'antd';
import { AxiosError } from 'axios';
import { FormChangeInfo } from 'rc-field-form/es/FormContext';
import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useRouteMatch } from 'react-router-dom';
import {
  useActiveInsurer,
  useInactiveInsurer,
  useUpdateInsurer,
} from '@/core/domain/people/Insurers/Insurers.hooks';
import handlerGeneralUpdate from './handlers/general';

type Props = PropsWithChildren<unknown>;
type Forms = {
  insurerGeneral?: InsurerFormValues;
};

type InsurerFormContextProps = {
  isLoading: boolean;
  forms: Forms;
  insurerId?: number;
  disabled: boolean;
  handleToggleActive: (status: boolean) => void;
  active: boolean;
  setActive: (param: boolean) => void;
  activateMutation: { isLoading: boolean };
  inactivateMutation: { isLoading: boolean };
};

const InsurerFormContext = createContext({} as InsurerFormContextProps);
export const useInsurerFormContext = () => useContext(InsurerFormContext);

const InsurerFormProvider: React.FC<Props> = ({ children }) => {
  const { sendFormErrorNotification } = useDefaultFormErrorNotification();
  const { params } = useRouteMatch<{ insurerId: string }>();

  // State
  const [forms, setForms] = useState<Forms>({});
  const [isLoading, setIsLoading] = useState(false);
  const [insurerId, setInsurerId] = useState<number>();
  const [active, setActive] = useState(false);

  // Request hook
  const generalUpdate = useUpdateInsurer();

  const activateMutation = useActiveInsurer();

  const inactivateMutation = useInactiveInsurer();

  const disabled = !Object.keys(forms).some((key) => !!key);

  useServiceError(generalUpdate.error);

  useEffect(() => {
    setForms({});
    if (params.insurerId) {
      setInsurerId(Number(params.insurerId));
    }
  }, [params.insurerId]);

  function handleFormChange(name: string, info: FormChangeInfo) {
    if (name && info?.forms[name]) {
      setForms((state) => ({
        ...state,
        [name]: info.forms[name]?.getFieldsValue(),
      }));
    }
  }

  async function handleSubmit() {
    setIsLoading(true);

    try {
      if (forms.insurerGeneral) {
        await handlerGeneralUpdate(forms.insurerGeneral, active, (values) =>
          generalUpdate.mutateAsync({
            ...values,
            insurerId: Number(params.insurerId),
          }),
        );
      }

      setForms({});
    } catch (error: unknown) {
      const err = error as AxiosError;

      if (!err.isAxiosError) {
        sendFormErrorNotification(error);
      }
    }

    setIsLoading(false);
  }

  const handleToggleActive = async (status: boolean) => {
    try {
      if (status) {
        await activateMutation.mutateAsync(Number(insurerId));
      } else {
        await inactivateMutation.mutateAsync(Number(insurerId));
      }
      setActive(status);
    } catch {}
  };

  return (
    <InsurerFormContext.Provider
      value={{
        isLoading,
        forms,
        insurerId,
        disabled,
        handleToggleActive,
        active,
        setActive,
        activateMutation,
        inactivateMutation,
      }}
    >
      <Form.Provider
        onFormFinish={handleSubmit}
        onFormChange={handleFormChange}
      >
        {children}
      </Form.Provider>
    </InsurerFormContext.Provider>
  );
};

export default InsurerFormProvider;
